// Hook for coordinating WebContainer preview across browser tabs
// Uses BroadcastChannel API to ensure only one tab has active preview

import { useState, useEffect, useCallback, useRef } from 'react';

const CHANNEL_NAME = 'pixel-preview-lock';

interface PreviewLockMessage {
    type: 'CLAIM' | 'RELEASE' | 'PING' | 'PONG';
    tabId: string;
    projectId?: string;
    timestamp: number;
}

// Generate unique tab ID
const generateTabId = () => `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

export function usePreviewLock(projectId: string | undefined) {
    const [isLocked, setIsLocked] = useState(false);
    const [lockingTabId, setLockingTabId] = useState<string | null>(null);
    const [hasPreviewLock, setHasPreviewLock] = useState(false);

    const tabIdRef = useRef<string>(generateTabId());
    const channelRef = useRef<BroadcastChannel | null>(null);
    const claimTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Claim the preview lock for this tab
    const claimLock = useCallback(() => {
        if (!channelRef.current || !projectId) return;

        const message: PreviewLockMessage = {
            type: 'CLAIM',
            tabId: tabIdRef.current,
            projectId,
            timestamp: Date.now(),
        };

        channelRef.current.postMessage(message);
        setHasPreviewLock(true);
        setIsLocked(false);
        setLockingTabId(null);

        console.log('[PreviewLock] Claimed lock for tab:', tabIdRef.current);
    }, [projectId]);

    // Release the preview lock
    const releaseLock = useCallback(() => {
        if (!channelRef.current) return;

        const message: PreviewLockMessage = {
            type: 'RELEASE',
            tabId: tabIdRef.current,
            timestamp: Date.now(),
        };

        channelRef.current.postMessage(message);
        setHasPreviewLock(false);

        console.log('[PreviewLock] Released lock for tab:', tabIdRef.current);
    }, []);

    useEffect(() => {
        // Check if BroadcastChannel is supported
        if (typeof BroadcastChannel === 'undefined') {
            console.warn('[PreviewLock] BroadcastChannel not supported, preview lock disabled');
            setHasPreviewLock(true); // Allow preview in unsupported browsers
            return;
        }

        // Create channel
        const channel = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = channel;

        // Handle incoming messages
        channel.onmessage = (event: MessageEvent<PreviewLockMessage>) => {
            const message = event.data;

            if (message.tabId === tabIdRef.current) return; // Ignore own messages

            switch (message.type) {
                case 'CLAIM':
                    // Another tab claimed the lock
                    if (hasPreviewLock) {
                        // We had the lock, now we don't
                        setHasPreviewLock(false);
                    }
                    setIsLocked(true);
                    setLockingTabId(message.tabId);
                    console.log('[PreviewLock] Lock claimed by another tab:', message.tabId);
                    break;

                case 'RELEASE':
                    // Another tab released the lock
                    if (message.tabId === lockingTabId) {
                        setIsLocked(false);
                        setLockingTabId(null);
                        console.log('[PreviewLock] Lock released by tab:', message.tabId);
                    }
                    break;

                case 'PING':
                    // Another tab is checking if we have the lock
                    if (hasPreviewLock) {
                        const pong: PreviewLockMessage = {
                            type: 'PONG',
                            tabId: tabIdRef.current,
                            projectId,
                            timestamp: Date.now(),
                        };
                        channel.postMessage(pong);
                    }
                    break;

                case 'PONG':
                    // Another tab responded - they have the lock
                    setIsLocked(true);
                    setLockingTabId(message.tabId);
                    break;
            }
        };

        // On mount, ping to check if any other tab has the lock
        const ping: PreviewLockMessage = {
            type: 'PING',
            tabId: tabIdRef.current,
            timestamp: Date.now(),
        };
        channel.postMessage(ping);

        // Wait a bit for PONG responses, then claim if no response
        claimTimeoutRef.current = setTimeout(() => {
            if (!isLocked && projectId) {
                claimLock();
            }
        }, 300);

        // Cleanup
        return () => {
            if (claimTimeoutRef.current) {
                clearTimeout(claimTimeoutRef.current);
            }
            if (hasPreviewLock) {
                releaseLock();
            }
            channel.close();
            channelRef.current = null;
        };
    }, [projectId]); // Re-run when projectId changes

    // Release lock on unmount or when navigating away
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (hasPreviewLock) {
                releaseLock();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasPreviewLock, releaseLock]);

    return {
        /** True if another tab has the preview lock */
        isLocked,
        /** True if this tab has the preview lock */
        hasPreviewLock,
        /** Claim the preview lock for this tab */
        claimLock,
        /** Release the preview lock */
        releaseLock,
        /** ID of the tab that has the lock (if locked) */
        lockingTabId,
        /** This tab's ID */
        tabId: tabIdRef.current,
    };
}
